// @flow
import type { Crop } from '../../../components/types'
import _ from 'lodash'

type CarouselCardErrors = {|
  urlErrors: Array<string>,
  titleErrors: Array<string>,
  descriptionErrors: Array<string>,
  imageErrors: Array<string>,
  vkLinkButtonErrors: Array<string>,
  priceErrors: Array<string>,
  oldPriceErrors: Array<string>
|}

export type CarouselCard = {|
  title: string,
  url: string,
  description: string,
  imageStoreId: ?string,
  crop: ?Crop,
  vkLinkButton: string,
  price: string,
  oldPrice: string,
  ...CarouselCardErrors
|}

export type Carousel = {|
  cards: Array<CarouselCard>,
  currentCardIndex: number,
  errors: Array<string>
|}

type State = {
  [string]: Carousel
}

type PatchAction = {|
  +type: 'CAROUSEL_EDIT.PATCH',
  +storeId: string,
  +state: $Shape<Carousel>
|}

type InitAction = {|
  +type: 'CAROUSEL_EDIT.INIT',
  +storeId: string,
  +state: Carousel
|}

type AddAction = {|
  +type: 'CAROUSEL_EDIT.ADD',
  +storeId: string
|}

type RemoveAction = {|
  +type: 'CAROUSEL_EDIT.REMOVE',
  +storeId: string,
  +index: number
|}

type Action = PatchAction | InitAction | AddAction | RemoveAction

export type { State }

const initialState: State = {}

const emptyCard: CarouselCard = {
  title: '',
  url: '',
  description: '',
  imageStoreId: null,
  crop: null,
  urlErrors: [],
  titleErrors: [],
  descriptionErrors: [],
  imageErrors: [],
  vkLinkButton: '',
  vkLinkButtonErrors: [],
  price: '',
  priceErrors: [],
  oldPrice: '',
  oldPriceErrors: []
}

export function patch(storeId: string, diff: $Shape<Carousel>): PatchAction {
  return {
    type: 'CAROUSEL_EDIT.PATCH',
    storeId,
    state: diff
  }
}

export function init(storeId: string, state: Carousel): InitAction {
  return {
    type: 'CAROUSEL_EDIT.INIT',
    storeId,
    state
  }
}

export function add(storeId: string): AddAction {
  return { type: 'CAROUSEL_EDIT.ADD', storeId }
}

export function remove(storeId: string, index: number): RemoveAction {
  return { type: 'CAROUSEL_EDIT.REMOVE', storeId, index }
}

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case 'CAROUSEL_EDIT.INIT':
      return { ...state, [action.storeId]: action.state }
    case 'CAROUSEL_EDIT.PATCH':
      return { ...state, [action.storeId]: { ...state[action.storeId], ...action.state } }
    case 'CAROUSEL_EDIT.ADD': {
      const cards = state[action.storeId].cards
      return {
        ...state,
        [action.storeId]: {
          ...state[action.storeId],
          cards: [...cards, emptyCard],
          errors: []
        }
      }
    }
    case 'CAROUSEL_EDIT.REMOVE': {
      const cards = _.cloneDeep(state[action.storeId].cards)
      const currentCardIndex = state[action.storeId].currentCardIndex
      cards.splice(action.index, 1)

      return {
        ...state,
        [action.storeId]: {
          ...state[action.storeId],
          cards,
          errors: [],
          currentCardIndex:
            currentCardIndex > action.index
              ? currentCardIndex - 1
              : _.max([0, _.min([currentCardIndex, cards.length - 1])])
        }
      }
    }
    default:
      return state
  }
}
